import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Fuse from 'fuse.js';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import FiltersBar from 'components/FiltersBar';
import GlassBeadCard from './GlassBeadCard/GlassBeadCard';
import PageLoader from 'components/PageLoader';
import { fetchGlassBeads } from 'store/supplies/suppliesOperations';
import suppliesActions from 'store/supplies/suppliesActions';
import styles from './styles';
import { withStyles } from '@material-ui/core/styles';
import SortingPanel from './SortingPanel/SortingPanel';
import { exportData, importData } from 'helpers/downloadFile';
import ArchiveDialog from '../ArchiveDialog';
import NoResults from '../NoResults';
import { impactedEntities, refetchSupplies } from '../../helpers/archive';

const searchOptions = {
  shouldSort: true,
  keys: ['name'],
};

const GlassBeads = ({ classes, onSelect, setSnackbar }) => {
  const dispatch = useDispatch();
  const [fuse, setFuse] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'name', sortOrder: 'asc' });
  const [archivingGlassBead, setArchivingGlassBead] = useState(null);

  const glassBeadsData = useSelector((state) => state.supplies.glassBeads.data);
  const snackbar = useSelector((state) => state.supplies.glassBeads.snackBarInfo);
  const loading = useSelector((state) => state.supplies.glassBeads.loading);

  useEffect(() => {
    dispatch(fetchGlassBeads());
  }, []);

  useEffect(() => {
    if (snackbar) {
      setSnackbar(snackbar);
      dispatch(suppliesActions.postGlassBeadError(false));
    }
  }, [snackbar]);

  const openDrawer = useCallback(
    () => () => {
      setDrawer({ isOpen: true });
    },
    []
  );

  const closeDrawer = useCallback(() => {
    setDrawer({ isOpen: false });
  }, []);

  const onUpdate = useCallback((values) => {
    setSorting(values);
    dispatch(fetchGlassBeads(values));
    closeDrawer();
  }, []);

  const onSortingCancel = useCallback(() => {
    setSorting({
      sortBy: 'name',
      sortOrder: 'asc',
    });
    dispatch(fetchGlassBeads());
    closeDrawer();
  }, []);

  const onArchived = useCallback((impact) => {
    refetchSupplies(dispatch, impactedEntities(impact).filter((entity) => entity !== 'glassBeads'));
    dispatch(fetchGlassBeads(sorting));
  }, [sorting]);

  const onExportExcel = () => {
    exportData(
      'glass-beads/csv',
      {
        searchValue,
      },
      (type, text) => {
        setSnackbar({
          type,
          text,
        });
      }
    );
  };

  const onImportExcel = async (data) => {
    importData('glass-beads/csv', data, (type, text) => {
      setSnackbar({
        type,
        text,
      });
    }).then((e) => dispatch(fetchGlassBeads()));
  };

  useMemo(() => {
    setFuse(new Fuse(glassBeadsData, searchOptions));
  }, [glassBeadsData]);

  const filteredGlassBeads = fuse && searchValue ? fuse.search(searchValue) : glassBeadsData;

  return loading ? (
    <PageLoader loading>
      <div style={{ height: 100 }} />
    </PageLoader>
  ) : (
    <>
      <FiltersBar
        openDrawer={openDrawer}
        updateSearchValue={setSearchValue}
        isDynamicSearch={true}
        hasImport={true}
        hasExport={true}
        onExportData={onExportExcel}
        onImportData={onImportExcel}
      />
      <Grid container spacing={2}>
        {filteredGlassBeads.map((m) => (
          <Grid key={m._id} item xs={12} md={6} lg={3}>
            <GlassBeadCard
              data={m}
              onArchive={setArchivingGlassBead}
              onClick={() => onSelect(m)}
            />
          </Grid>
        ))}
      </Grid>
      {!filteredGlassBeads.length && <NoResults />}
      {drawer && (
        <Drawer anchor="right" open={drawer.isOpen}>
          <SortingPanel
            closeDrawer={closeDrawer}
            onCancel={onSortingCancel}
            sorting={sorting}
            onUpdate={onUpdate}
          />
        </Drawer>
      )}
      {archivingGlassBead && (
        <ArchiveDialog
          entity="glassBeads"
          item={archivingGlassBead}
          onClose={() => setArchivingGlassBead(null)}
          onDone={onArchived}
          setSnackbar={setSnackbar}
        />
      )}
    </>
  );
};

export default withStyles(styles)(GlassBeads);
